import React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import Datadog from "@analytics/DatadogUtils";
import { fetchVideoToken } from "api/videoToken";
import { getVideoTokenEnabled, getVideoTokenPeriod } from "environment";
import { useLayoutEffectOnce } from "src/utils/miniReactUse";
import { changeUsername } from "state/actionCreators/connectionManager";
import { RootState } from "state/delegate";
import { connectionManagerSelectors } from "state/selectors";

const MIN_DURATION_BETWEEN_TOKEN_FETCH_MILLISECONDS = 5000;

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const selector = (state: RootState) => ({
  usernameData: connectionManagerSelectors.getUsername(state),
});

const VideoTokenLoader: React.FC = () => {
  const dispatch = useDispatch();
  const { usernameData } = useSelector(selector, shallowEqual);

  useLayoutEffectOnce(() => {
    if (!getVideoTokenEnabled()) {
      return;
    }

    // Implementation note: Chrome will stop calling `setInterval` when tab in switched
    // after some time. Other places like rich fragment are using `setTimeout` and
    // keep working when tab is in background.
    // Potential further enhancement: move the request loop to a WebWorker.
    const requestLoop = async (signal: AbortSignal) => {
      while (!signal.aborted) {
        const start = performance.now();

        try {
          const { username } = await fetchVideoToken();
          Datadog.setGlobalContextProperty("username", username);

          if (username && usernameData !== username) {
            dispatch(changeUsername(username));
          }
        } catch {
          // noop
        }

        const end = performance.now();

        const durationMilliseconds = end - start;

        const delayDurationMilliseconds = Math.min(
          MIN_DURATION_BETWEEN_TOKEN_FETCH_MILLISECONDS,
          Math.ceil(getVideoTokenPeriod() - durationMilliseconds)
        );

        await delay(delayDurationMilliseconds);
      }
    };

    const abort = new AbortController();
    requestLoop(abort.signal);

    return () => {
      abort.abort();
    };
  });

  return null;
};

export default VideoTokenLoader;
